/**
 * ApartmentHeatSourcesSection — „Źródła ciepła w lokalu" w formularzu lokalu panelu (Bronek 2026-10-12,
 * kontrakt-API #416 pkt 3). Admin zaznacza „podłączony do wspólnego źródła budynku" / „brak źródła
 * ciepła", dodaje źródło (typ + charakter, opcjonalnie pomieszczenie), usuwa źródło i — od zadania 4 (2026-10-21,
 * przegląd z papieru wprowadzany w panelu „jak na tablecie") — EDYTUJE istniejące źródło: charakter, lokalizację
 * i parametry z formularza tabletu (HeatSourceParamsFields, lustro utils/heatSourceParams).
 *
 * Komponent tylko edytuje stan części — zapis (i decyzja, które pola wysłać) należy do formularza
 * (ApartmentInspectionForm + utils/apartmentHeatSources.heatPayload). Reguły spójności: lustro tabletu
 * w utils/apartmentHeatSources. Edycja zmienia wyłącznie pola, które admin ruszył (heatParamChanges) — pozostałe
 * parametry z tabletu zostają w danych nietknięte.
 *
 * Dodawanie i edycja są inline, nie w osobnym ModalShell: zagnieżdżony ModalShell zamykałby Esc-em także
 * formularz lokalu (oba słuchają Esc na `document`) razem z niezapisanymi zmianami.
 */
import React, { useState } from 'react';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { ConfirmModal } from '../ds/ConfirmModal';
import { HEAT_CHARACTER_LABELS, HEAT_SOURCE_TYPE_LABELS, resolveHeatCharacter, resolveHeatSourceType } from '../ds/HeatSourceLabels';
import { ROOM_OPTIONS } from './RecommendationDetails/PgCogDetails';
import { HeatSourceDetails } from './HeatSourceDetails';
import { HeatSourceParamsFields } from './HeatSourceParamsFields';
import { heatSourceDisplayNames } from '../utils/heatSourceNames';
import { heatParamChanges } from '../utils/heatSourceParams';
import {
  APARTMENT_HEAT_SOURCE_TYPES,
  ApartmentHeatState,
  HEAT_CHARACTER_KEYS_BY_TYPE,
  addHeatSource,
  hasHeatCharacter,
  isDisplayableHeatSource,
  newApartmentHeatSource,
  removeHeatSourceAt,
  setNoHeatSource,
  setUsesBuildingHeat,
  updateHeatSourceAt,
} from '../utils/apartmentHeatSources';

interface Props {
  value: ApartmentHeatState;
  onChange: (next: ApartmentHeatState) => void;
  /** Część zmieniona i jeszcze niezapisana — podpowiedź pod nagłówkiem. */
  dirty?: boolean;
  /**
   * Rekordu tego przeglądu jeszcze nie ma, a lista pochodzi z poprzedniego przeglądu lokalu. Bez zmiany
   * w sekcji zapis jej nie przenosi (formularz wysyła tylko zmienione pola) — mówimy to wprost.
   */
  fromPreviousInspection?: boolean;
  disabled?: boolean;
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-secondary)',
};

const noteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-label)',
  color: 'var(--text-secondary)',
};

function chipStyle(active: boolean): React.CSSProperties {
  return {
    minWidth: 0,
    padding: 'var(--space-2) var(--space-3)',
    textAlign: 'center',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-label)',
    fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
    borderRadius: 'var(--radius-sm)',
    border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border)'}`,
    background: active ? 'var(--changed-bg)' : 'var(--surface-fill)',
    color: active ? 'var(--changed-text)' : 'var(--text-primary)',
  };
}

const chipGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 'var(--space-2)',
};

export function ApartmentHeatSourcesSection({ value, onChange, dirty = false, fromPreviousInspection = false, disabled = false }: Props) {
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState('');
  const [newCharacter, setNewCharacter] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);
  const [confirmNoSource, setConfirmNoSource] = useState(false);
  // Edycja istniejącego źródła (jedno naraz): pozycja w liście z serwera + robocze wartości params i charakter.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, unknown>>({});
  const [editCharacter, setEditCharacter] = useState('');

  // Wpisy z typem (pozostałe zostają w danych, ale nie ma czego pokazać) z pozycją w liście z serwera.
  const visible = value.heatSources
    .map((hs, index) => ({ hs, index }))
    .filter((x): x is { hs: typeof x.hs & { type: string }; index: number } => isDisplayableHeatSource(x.hs));
  // Nazwy jak w zaleceniach (typ + pomieszczenie, numeracja powtórzeń) — do pytania o usunięcie.
  const names = heatSourceDisplayNames(
    visible.map(({ hs, index }) => ({
      id: String(index),
      type: hs.type,
      params: hs.params && typeof hs.params === 'object' ? (hs.params as Record<string, unknown>) : undefined,
    })),
  );

  const resetAdd = () => {
    setAdding(false);
    setNewType('');
    setNewCharacter('');
    setNewLocation('');
    setAddError(null);
  };

  const handleAdd = () => {
    const withCharacter = hasHeatCharacter(newType);
    if (!newType || (withCharacter && !newCharacter)) {
      setAddError(withCharacter || !newType ? 'Wybierz rodzaj źródła i charakter ciepła.' : 'Wybierz rodzaj źródła.');
      return;
    }
    onChange(addHeatSource(value, newApartmentHeatSource(newType, newCharacter, newLocation || undefined)));
    resetAdd();
  };

  const toggleNoSource = () => {
    if (value.noHeatSource) {
      onChange(setNoHeatSource(value, false));
    } else if (visible.length > 0) {
      setConfirmNoSource(true); // nie kasujemy źródeł po cichu (lustro tabletu, M3)
    } else {
      onChange(setNoHeatSource(value, true));
    }
  };

  const characterKeys = HEAT_CHARACTER_KEYS_BY_TYPE[newType] || [];

  const paramsOf = (hs: { params?: unknown }): Record<string, unknown> =>
    (hs.params && typeof hs.params === 'object' ? (hs.params as Record<string, unknown>) : {});
  const characterOf = (hs: Record<string, unknown>): string =>
    String(hs.heatCharacter ?? hs.heat_character ?? paramsOf(hs).heatCharacter ?? '');

  const startEdit = (index: number) => {
    const hs = value.heatSources[index] as Record<string, unknown>;
    setEditIndex(index);
    setEditValues({ ...paramsOf(hs) });
    setEditCharacter(characterOf(hs));
    setAdding(false);
  };
  const cancelEdit = () => setEditIndex(null);
  const applyEdit = () => {
    if (editIndex === null) return;
    const hs = value.heatSources[editIndex] as Record<string, unknown>;
    onChange(updateHeatSourceAt(value, editIndex, {
      heatCharacter: editCharacter || undefined,
      paramChanges: heatParamChanges(paramsOf(hs), editValues),
    }));
    setEditIndex(null);
  };

  return (
    <section aria-label="Źródła ciepła w lokalu" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <Icon name="flame" size={20} color="var(--text-secondary)" />
        <span style={labelStyle}>Źródła ciepła w lokalu</span>
      </div>
      {dirty ? (
        <p style={noteStyle}>Zmiany źródeł ciepła zapiszą się razem z przeglądem lokalu.</p>
      ) : fromPreviousInspection && (
        <p style={noteStyle} data-testid="apartment-heat-from-previous">
          Źródła z poprzedniego przeglądu — w tym przeglądzie zapiszą się dopiero po zmianie w tej sekcji.
        </p>
      )}

      <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value.usesBuildingHeat === true}
          disabled={disabled}
          onChange={(e) => onChange(setUsesBuildingHeat(value, e.target.checked))}
        />
        <span>Lokal podłączony do wspólnego źródła ciepła budynku</span>
      </label>
      <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
        <input type="checkbox" checked={value.noHeatSource === true} disabled={disabled} onChange={toggleNoSource} />
        <span>Brak źródła ciepła</span>
      </label>

      {value.noHeatSource ? (
        <p style={noteStyle}>Zaznaczono „Brak źródła ciepła” — lokal nie ma własnych źródeł.</p>
      ) : (
        <>
          {value.usesBuildingHeat && (
            <p style={noteStyle}>Lokal korzysta ze wspólnego źródła budynku — własne źródła (np. kominek) można dodać obok.</p>
          )}

          {visible.length > 0 ? (
            <div
              style={{
                border: 'var(--border-width) solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
            >
              {visible.map(({ hs, index }, i) => {
                const character = hasHeatCharacter(hs.type) ? resolveHeatCharacter(String(hs.heatCharacter ?? hs.heat_character ?? '')) : '';
                const editing = editIndex === index;
                const editCharacterKeys = HEAT_CHARACTER_KEYS_BY_TYPE[hs.type] || [];
                return (
                  <React.Fragment key={typeof hs.id === 'string' || typeof hs.id === 'number' ? String(hs.id) : `idx-${index}`}>
                  <div
                    data-testid="apartment-heat-source"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--space-4)',
                      padding: 'var(--space-3) var(--space-4)',
                      borderBottom: i < visible.length - 1 || editing ? 'var(--border-width) solid var(--border)' : 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-body)', fontWeight: 'var(--weight-medium)', color: 'var(--text-primary)' }}>
                        {i + 1}. {resolveHeatSourceType(hs.type)}
                      </div>
                      {character && (
                        <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 4 }}>{character}</div>
                      )}
                      {/* Zapisane parametry (z tabletu albo z edycji w panelu). */}
                      <HeatSourceDetails
                        type={hs.type}
                        params={hs.params && typeof hs.params === 'object' ? (hs.params as Record<string, unknown>) : null}
                      />
                    </div>
                    <button
                      type="button"
                      title="Edytuj źródło ciepła"
                      aria-label={`Edytuj źródło ciepła: ${names[String(index)] ?? resolveHeatSourceType(hs.type)}`}
                      aria-expanded={editIndex === index}
                      disabled={disabled}
                      onClick={() => (editIndex === index ? cancelEdit() : startEdit(index))}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--action-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="pencil" size={22} />
                    </button>
                    <button
                      type="button"
                      title="Usuń źródło ciepła"
                      aria-label={`Usuń źródło ciepła: ${names[String(index)] ?? resolveHeatSourceType(hs.type)}`}
                      disabled={disabled}
                      onClick={() => setRemoveIndex(index)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--status-deny)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="trash" size={22} />
                    </button>
                  </div>
                  {editing && (
                    <div
                      data-testid="apartment-heat-source-edit"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        background: 'var(--surface-card)',
                        borderBottom: i < visible.length - 1 ? 'var(--border-width) solid var(--border)' : 'none',
                      }}
                    >
                      <p style={noteStyle}>Te same pola co na tablecie. Zmiany zapiszą się razem z przeglądem lokalu.</p>
                      {hasHeatCharacter(hs.type) && (
                        <>
                          <span style={labelStyle}>Charakter ciepła</span>
                          <div role="group" aria-label="Charakter ciepła (edycja)" style={chipGrid}>
                            {editCharacterKeys.map((key) => (
                              <button
                                key={key}
                                type="button"
                                aria-pressed={editCharacter === key}
                                onClick={() => setEditCharacter(key)}
                                style={chipStyle(editCharacter === key)}
                              >
                                {HEAT_CHARACTER_LABELS[key] ?? key}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      <HeatSourceParamsFields
                        idPrefix={`apt-hs-edit-${index}`}
                        type={hs.type}
                        values={editValues}
                        onChange={(key, v) => setEditValues((cur) => ({ ...cur, [key]: v }))}
                        disabled={disabled}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={cancelEdit}>Anuluj</Button>
                        <Button variant="primary" icon="check" onClick={applyEdit}>Zastosuj zmiany</Button>
                      </div>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            !value.usesBuildingHeat && <p style={noteStyle}>Brak zapisanych źródeł ciepła lokalu.</p>
          )}

          {adding ? (
            <div
              data-testid="apartment-heat-source-add"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                border: 'var(--border-width) solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-card)',
              }}
            >
              {addError && (
                <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--status-deny)', fontSize: 'var(--text-label)' }}>
                  <Icon name="triangle-alert" size={18} />
                  <span>{addError}</span>
                </div>
              )}
              <span style={labelStyle}>Rodzaj źródła ciepła</span>
              <div role="group" aria-label="Rodzaj źródła ciepła" style={chipGrid}>
                {APARTMENT_HEAT_SOURCE_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={newType === type}
                    onClick={() => {
                      setNewType(type);
                      setAddError(null);
                      // Charakter spoza opcji nowego typu (np. M.O.P. przy pompie ciepła) — wybór od nowa.
                      if (!(HEAT_CHARACTER_KEYS_BY_TYPE[type] || []).includes(newCharacter)) setNewCharacter('');
                    }}
                    style={chipStyle(newType === type)}
                  >
                    {HEAT_SOURCE_TYPE_LABELS[type] ?? type}
                  </button>
                ))}
              </div>

              {/* Typy bez „Charakteru” w CEEB (HEAT_TYPES_WITHOUT_CHARACTER) — bez tego wyboru, jak na tablecie. */}
              {newType && hasHeatCharacter(newType) && (
                <>
                  <span style={labelStyle}>Charakter ciepła</span>
                  <div role="group" aria-label="Charakter ciepła" style={chipGrid}>
                    {characterKeys.map((key) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={newCharacter === key}
                        onClick={() => { setNewCharacter(key); setAddError(null); }}
                        style={chipStyle(newCharacter === key)}
                      >
                        {HEAT_CHARACTER_LABELS[key] ?? key}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label htmlFor="apartment-heat-source-location" style={labelStyle}>
                  Lokalizacja (pomieszczenie) — opcjonalnie
                </label>
                <select
                  id="apartment-heat-source-location"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: 'var(--touch-min)',
                    padding: '0 var(--space-4)',
                    boxSizing: 'border-box',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-body)',
                    color: 'var(--text-primary)',
                    background: 'var(--surface-card)',
                    border: 'var(--border-width-strong) solid var(--border-strong)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <option value="">— nie podano —</option>
                  {ROOM_OPTIONS.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={resetAdd}>Anuluj</Button>
                <Button variant="primary" icon="plus" onClick={handleAdd}>Dodaj do lokalu</Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" icon="plus" onClick={() => { setEditIndex(null); setAdding(true); }} disabled={disabled}>
              Dodaj źródło ciepła
            </Button>
          )}
        </>
      )}

      <ConfirmModal
        open={removeIndex !== null}
        title="Usunąć źródło ciepła?"
        message={removeIndex !== null
          ? `„${names[String(removeIndex)] ?? 'Źródło ciepła'}” zostanie usunięte z lokalu po zapisaniu przeglądu.`
          : ''}
        confirmLabel="Usuń"
        cancelLabel="Anuluj"
        destructive
        onConfirm={() => {
          const index = removeIndex;
          setRemoveIndex(null);
          if (index !== null) {
            setEditIndex(null); // pozycje w liście przesuwają się — otwarta edycja wskazywałaby inne źródło
            onChange(removeHeatSourceAt(value, index));
          }
        }}
        onCancel={() => setRemoveIndex(null)}
      />

      <ConfirmModal
        open={confirmNoSource}
        title="Usunąć źródła ciepła?"
        message={`Zaznaczenie „Brak źródła ciepła” usunie ${visible.length} dodane źródło/źródła ciepła lokalu. Kontynuować?`}
        confirmLabel="Usuń"
        cancelLabel="Anuluj"
        destructive
        onConfirm={() => {
          setConfirmNoSource(false);
          setEditIndex(null);
          onChange(setNoHeatSource(value, true));
        }}
        onCancel={() => setConfirmNoSource(false)}
      />
    </section>
  );
}
